import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Zap, Play, Pause, Activity } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString();
}

export default function SimulatorPanel() {
  const [status, setStatus] = useState(null);
  const [ticking, setTicking] = useState(false);

  const load = async () => {
    const { data } = await api.get("/simulator/status");
    setStatus(data);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);

  const manualTick = async () => {
    setTicking(true);
    try {
      const { data } = await api.post("/simulator/tick");
      toast.success(`Injected ${data.events_generated} events across ${data.touched} deals`);
      load();
    } catch {
      toast.error("Tick failed");
    } finally {
      setTicking(false);
    }
  };

  const togglePaused = async (nextPaused) => {
    try {
      const { data } = await api.patch("/simulator/config", { paused: nextPaused });
      setStatus(data);
      toast.success(nextPaused ? "Simulator paused" : "Simulator resumed");
    } catch {
      toast.error("Could not update simulator");
    }
  };

  if (!status) return null;

  return (
    <div className="border border-gray-200 bg-white rounded-sm p-6" data-testid="simulator-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-gray-500" strokeWidth={2} />
          <span className="overline text-gray-400">Live Event Simulator</span>
        </div>
        <span
          className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${
            status.running && !status.paused
              ? "bg-green-50 text-green-700 border-green-200"
              : "bg-gray-50 text-gray-500 border-gray-200"
          }`}
          data-testid="simulator-state"
        >
          {status.running && !status.paused ? "RUNNING" : status.paused ? "PAUSED" : "STOPPED"}
        </span>
      </div>
      <p className="text-xs text-gray-500 mt-1 mb-5 leading-relaxed">
        Generates synthetic CRM events on demo deals every {status.tick_seconds}s so the FFT layer has real data to score. Disable when connecting a real HubSpot/Salesforce feed.
      </p>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="border border-gray-200 rounded-sm p-4">
          <div className="overline text-gray-400 text-[10px] mb-1">Ticks total</div>
          <div className="metric-value text-2xl">{status.ticks_total}</div>
        </div>
        <div className="border border-gray-200 rounded-sm p-4">
          <div className="overline text-gray-400 text-[10px] mb-1">Events generated</div>
          <div className="metric-value text-2xl">{status.events_generated}</div>
        </div>
        <div className="border border-gray-200 rounded-sm p-4">
          <div className="overline text-gray-400 text-[10px] mb-1">Last tick</div>
          <div className="text-sm font-mono truncate">{fmtTime(status.last_tick_at)}</div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center gap-3">
          <span className="overline text-gray-400 text-[10px]">Enabled</span>
          <Switch
            checked={!status.paused}
            onCheckedChange={(v) => togglePaused(!v)}
            data-testid="simulator-toggle"
          />
        </div>
        <Button
          onClick={manualTick}
          disabled={ticking}
          variant="outline"
          size="sm"
          className="rounded-sm h-8 gap-2"
          data-testid="manual-tick-btn"
        >
          <Zap className={`w-3 h-3 ${ticking ? "animate-pulse" : ""}`} />
          Inject events now
        </Button>
      </div>
    </div>
  );
}
